import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Shield, Plus, Trash2, Loader2, AlertTriangle, Bell, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
var alertTypeColors = { echeance: "bg-amber-100 text-amber-700", renouvellement: "bg-blue-100 text-blue-700", clause_sensible: "bg-red-100 text-red-700" };
var alertTypeLabels = { echeance: "Échéance", renouvellement: "Renouvellement", clause_sensible: "Clause sensible" };
var priorityColors = { faible: "bg-slate-100 text-slate-600", normal: "bg-blue-100 text-blue-700", urgent: "bg-red-100 text-red-700" };

function AlertForm({ contracts, onSave, onClose }) {
  var [form, setForm] = useState({ contract_id: "", alert_type: "echeance", title: "", description: "", due_date: "", priority: "normal" });
  var [saving, setSaving] = useState(false);

  function set(f, v) { setForm(function(p) { return Object.assign({}, p, { [f]: v }); }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.contract_id || !form.title || !form.due_date) { toast.error("Contrat, titre et date requis"); return; }
    setSaving(true);
    try {
      var res = await fetch(API_URL + "/api/contract-alerts", {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(form)
      });
      if (res.ok) { toast.success("Alerte créée"); onSave(); onClose(); }
      else { var err = await res.json(); toast.error(err.detail || "Erreur"); }
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  }

  var contractOptions = [];
  for (var i = 0; i < contracts.length; i++) {
    var c = contracts[i];
    contractOptions.push(<SelectItem key={c.contract_id} value={c.contract_id}>{c.title}</SelectItem>);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><Label>Contrat</Label>
          <Select value={form.contract_id} onValueChange={function(v) { set("contract_id", v); }}>
            <SelectTrigger data-testid="alert-contract"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
            <SelectContent>{contractOptions}</SelectContent>
          </Select>
        </div>
        <div><Label>Type</Label>
          <Select value={form.alert_type} onValueChange={function(v) { set("alert_type", v); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="echeance">Échéance</SelectItem><SelectItem value="renouvellement">Renouvellement</SelectItem><SelectItem value="clause_sensible">Clause sensible</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Priorité</Label>
          <Select value={form.priority} onValueChange={function(v) { set("priority", v); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="faible">Faible</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Titre</Label><Input value={form.title} onChange={function(e) { set("title", e.target.value); }} data-testid="alert-title" /></div>
        <div className="col-span-2"><Label>Date d'échéance</Label><Input type="date" value={form.due_date} onChange={function(e) { set("due_date", e.target.value); }} data-testid="alert-date" /></div>
        <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={function(e) { set("description", e.target.value); }} /></div>
      </div>
      <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Annuler</Button><Button type="submit" disabled={saving} data-testid="save-alert-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Créer</Button></DialogFooter>
    </form>
  );
}

export default function Surveillance({ user, setUser }) {
  var [alerts, setAlerts] = useState([]);
  var [contracts, setContracts] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showForm, setShowForm] = useState(false);

  useEffect(function() {
    fetchAlerts();
    fetch(API_URL + "/api/contracts", { credentials: "include" }).then(function(r) { return r.json(); }).then(setContracts).catch(function() {});
  }, []);

  async function fetchAlerts() {
    try { var res = await fetch(API_URL + "/api/contract-alerts", { credentials: "include" }); if (res.ok) setAlerts(await res.json()); } catch (e) { }
    setLoading(false);
  }

  async function resolveAlert(alertId) {
    var fd = new FormData(); fd.append("status", "resolved");
    await fetch(API_URL + "/api/contract-alerts/" + alertId, { method: "PUT", credentials: "include", body: fd });
    toast.success("Alerte résolue"); fetchAlerts();
  }

  async function deleteAlert(alertId) {
    await fetch(API_URL + "/api/contract-alerts/" + alertId, { method: "DELETE", credentials: "include" });
    toast.success("Supprimée"); fetchAlerts();
  }

  var active = alerts.filter(function(a) { return a.status === "active"; });
  var resolved = alerts.filter(function(a) { return a.status === "resolved"; });

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="surveillance-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Surveillance des contrats</h1>
            <p className="text-sm text-muted-foreground">Alertes et rappels sur vos contrats</p>
          </div>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild><Button data-testid="add-alert-btn"><Plus className="w-4 h-4 mr-2" />Nouvelle alerte</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Nouvelle alerte</DialogTitle><DialogDescription>Configurez une alerte sur un contrat</DialogDescription></DialogHeader>
              <AlertForm contracts={contracts} onSave={fetchAlerts} onClose={function() { setShowForm(false); }} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground">Alertes actives</p><p className="text-2xl font-bold text-red-600">{active.length}</p></div><AlertTriangle className="w-8 h-8 text-red-200" /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground">Résolues</p><p className="text-2xl font-bold text-green-600">{resolved.length}</p></div><CheckCircle className="w-8 h-8 text-green-200" /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{alerts.length}</p></div><Bell className="w-8 h-8 text-blue-200" /></div></CardContent></Card>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <Card><CardContent className="py-16 text-center"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-1">Aucune alerte</h3><p className="text-sm text-muted-foreground">Configurez des alertes pour vos contrats</p></CardContent></Card>
            ) : alerts.map(function(a) {
              return (
                <div key={a.alert_id} className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 bg-white" data-testid={"alert-" + a.alert_id}>
                  <div className={"w-10 h-10 rounded-lg flex items-center justify-center " + (a.status === "active" ? "bg-red-50" : "bg-green-50")}>
                    {a.status === "active" ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-green-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{a.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <Badge className={alertTypeColors[a.alert_type] + " text-xs"}>{alertTypeLabels[a.alert_type]}</Badge>
                      <span>{a.contract_title}</span>
                      <span>Échéance: {a.due_date}</span>
                      <Badge className={priorityColors[a.priority] + " text-xs"}>{a.priority}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {a.status === "active" && <Button variant="outline" size="sm" className="text-xs h-7" onClick={function() { resolveAlert(a.alert_id); }} data-testid={"resolve-" + a.alert_id}>Résoudre</Button>}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={function() { deleteAlert(a.alert_id); }}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
